import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Eye, Mail, Phone, Building, Trash2, Search, Filter, X, FileDown, AlertTriangle, Printer } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { openPrintWindow, getCompanyHeader, getDocumentInfo, getDataTable, getTotalSection, getFooter } from '../utils/PrintUtils';

const Clients = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('all'); // all, positive, negative, zero
  const [sortBy, setSortBy] = useState('name'); // name, balance, created_at
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    billing_address: '',
    tax_id: '',
    is_problematic: false,
    problematic_reason: ''
  });

  // Helper function to format error messages
  const getErrorMessage = (error, defaultMessage) => {
    const errorDetail = error.response?.data?.detail;
    if (typeof errorDetail === 'string') {
      return errorDetail;
    } else if (Array.isArray(errorDetail)) {
      return errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
    } else if (errorDetail && typeof errorDetail === 'object') {
      return JSON.stringify(errorDetail);
    }
    return defaultMessage;
  };

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchTerm, balanceFilter, sortBy, sortOrder]);

  const loadClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Müşteriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortClients = () => {
    let filtered = [...clients];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(client => 
        client.name?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone?.toLowerCase().includes(term) ||
        client.contact_person?.toLowerCase().includes(term) ||
        client.tax_id?.toLowerCase().includes(term)
      );
    }

    // Balance filter
    if (balanceFilter !== 'all') {
      filtered = filtered.filter(client => {
        const balance = client.balance || 0;
        if (balanceFilter === 'positive') return balance > 0;
        if (balanceFilter === 'negative') return balance < 0;
        if (balanceFilter === 'zero') return balance === 0;
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'balance':
          aValue = a.balance || 0;
          bValue = b.balance || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredClients(filtered);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setBalanceFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await api.put(`/clients/${selectedClient.id}`, formData);
        toast.success('Müşteri başarıyla güncellendi');
      } else {
        await api.post('/clients', formData);
        toast.success('Müşteri başarıyla oluşturuldu');
      }
      setShowModal(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Müşteri kaydedilemedi'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      billing_address: '',
      tax_id: '',
      is_problematic: false,
      problematic_reason: ''
    });
    setSelectedClient(null);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      billing_address: client.billing_address || '',
      tax_id: client.tax_id || '',
      is_problematic: client.is_problematic || false,
      problematic_reason: client.problematic_reason || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId, clientName) => {
    if (!window.confirm(`"${clientName}" müşterisini silmek istediğinizden emin misiniz?`)) return;
    
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Müşteri başarıyla silindi');
      loadClients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Müşteri silinemedi'));
    }
  };

  const handleViewLedger = async (client) => {
    try {
      setSelectedClient(client);
      const response = await api.get(`/clients/${client.id}/ledger`);
      setLedgerData(response.data);
      setShowLedger(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cari hesap yüklenemedi'));
    }
  };

  const printLedger = () => {
    if (!ledgerData || !selectedClient) {
      toast.error('Yazdırılacak veri bulunamadı');
      return;
    }
    
    // PrintUtils kullanarak cari hesap ekstresi yazdır
    const header = getCompanyHeader({
      company_name: 'ROTA Maritime Agency'
    }, 'CARİ HESAP EKSTRESİ');
    
    const clientInfoFields = [
      { label: 'Müşteri', value: selectedClient.name },
      ...(selectedClient.contact_person ? [{ label: 'İletişim', value: selectedClient.contact_person }] : []),
      ...(selectedClient.email ? [{ label: 'E-posta', value: selectedClient.email }] : []),
      ...(selectedClient.phone ? [{ label: 'Telefon', value: selectedClient.phone }] : []),
      ...(selectedClient.tax_id ? [{ label: 'Vergi No', value: selectedClient.tax_id }] : []),
      { label: 'Rapor Tarihi', value: new Date().toLocaleDateString('tr-TR') }
    ];
    const info = getDocumentInfo(clientInfoFields);
    
    const tableHeaders = [
      { label: 'Tarih', align: 'left' },
      { label: 'Açıklama', align: 'left' },
      { label: 'Tip', align: 'left' },
      { label: 'Borç', align: 'right' },
      { label: 'Alacak', align: 'right' },
      { label: 'Bakiye', align: 'right' }
    ];
    
    const tableRows = (ledgerData.entries || []).map(entry => [
      entry.date ? new Date(entry.date).toLocaleDateString('tr-TR') : '-',
      entry.description || '-',
      entry.type === 'invoice' ? 'Fatura' : entry.type === 'payment' ? 'Ödeme' : (entry.type || '-'),
      `<span style="color: #dc2626">${(entry.debit || 0) > 0 ? (entry.debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' ' + (entry.currency || 'USD') : '-'}</span>`,
      `<span style="color: #16a34a">${(entry.credit || 0) > 0 ? (entry.credit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' ' + (entry.currency || 'USD') : '-'}</span>`,
      (entry.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' ' + (entry.currency || 'USD')
    ]);
    
    const table = getDataTable(tableHeaders, tableRows);
    
    const totals = getTotalSection([
      { label: 'Toplam Borç (Faturalar)', value: ledgerData.total_invoiced || 0 },
      { label: 'Toplam Alacak (Ödemeler)', value: ledgerData.total_paid || 0 },
      { label: 'GÜNCEL BAKİYE', value: ledgerData.balance || 0, isTotal: true }
    ], 'USD');
    
    const footer = getFooter({ company_name: 'ROTA Maritime Agency' });
    
    const content = `
      <div style="max-width: 900px; margin: 0 auto; padding: 20px;">
        ${header}
        ${info}
        ${table}
        ${totals}
        ${footer}
      </div>
    `;
    
    openPrintWindow(content, `Cari Hesap - ${selectedClient.name}`);
  };

  const exportToExcel = () => {
    try {
      // Filtrelenmiş müşteri verilerini hazırla
      const excelData = filteredClients.map((client, index) => ({
        'Sıra': index + 1,
        'Müşteri Adı': client.name || '-',
        'İletişim Kişisi': client.contact_person || '-',
        'E-posta': client.email || '-',
        'Telefon': client.phone || '-',
        'Vergi No': client.tax_id || '-',
        'Fatura Adresi': client.billing_address || '-',
        'Bakiye (TRY)': client.balance || 0,
        'Yıllık Proforma': client.yearly_proforma_count || 0,
        'Yıllık Fatura': client.yearly_invoice_count || 0,
        'Oluşturma Tarihi': client.created_at ? new Date(client.created_at).toLocaleDateString('tr-TR') : '-'
      }));

      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Kolon genişliklerini ayarla
      ws['!cols'] = [
        { wch: 6 },  // Sıra
        { wch: 25 }, // Müşteri Adı
        { wch: 20 }, // İletişim Kişisi
        { wch: 25 }, // E-posta
        { wch: 15 }, // Telefon
        { wch: 15 }, // Vergi No
        { wch: 30 }, // Fatura Adresi
        { wch: 15 }, // Bakiye
        { wch: 15 }, // Yıllık Proforma
        { wch: 15 }, // Yıllık Fatura
        { wch: 15 }  // Oluşturma Tarihi
      ];

      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Müşteriler');

      // Dosya adı oluştur
      const fileName = `musteriler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(wb, fileName);
      
      toast.success(`${filteredClients.length} müşteri Excel'e aktarıldı`);
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel\'e aktarım başarısız oldu');
    }
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">{t('clients.title')}</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className="btn btn-secondary" 
            onClick={exportToExcel}
            data-testid="export-excel-btn"
            disabled={filteredClients.length === 0}
          >
            <FileDown size={18} />
            {t('reports.exportExcel')}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)}
            data-testid="add-client-btn"
          >
            <Plus size={18} />
            Add Client
          </button>
        </div>
      </div>
      
      <div style={{ padding: '2rem' }}>
        {/* Search and Filter Section */}
        {clients.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-body" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                {/* Search Bar */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search 
                      size={18} 
                      style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: '#64748b'
                      }} 
                    />
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Müşteri adı, email, telefon veya vergi numarası ile ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ paddingLeft: '2.5rem' }}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: '4px'
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Filters */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Filter size={18} style={{ color: '#64748b' }} />
                    <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 500 }}>
                      Filtreler:
                    </span>
                  </div>

                  <select
                    className="form-select"
                    value={balanceFilter}
                    onChange={(e) => setBalanceFilter(e.target.value)}
                    style={{ width: 'auto', minWidth: '150px' }}
                  >
                    <option value="all">Tüm Bakiyeler</option>
                    <option value="positive">Alacaklı (₺ &gt; 0)</option>
                    <option value="negative">Borçlu (₺ &lt; 0)</option>
                    <option value="zero">Sıfır (₺ = 0)</option>
                  </select>

                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{ width: 'auto', minWidth: '150px' }}
                  >
                    <option value="name">Ada Göre</option>
                    <option value="balance">Bakiyeye Göre</option>
                    <option value="created_at">Tarihe Göre</option>
                  </select>

                  <select
                    className="form-select"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    style={{ width: 'auto', minWidth: '120px' }}
                  >
                    <option value="asc">Artan</option>
                    <option value="desc">Azalan</option>
                  </select>

                  {(searchTerm || balanceFilter !== 'all' || sortBy !== 'name' || sortOrder !== 'asc') && (
                    <button
                      onClick={handleClearFilters}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      <X size={16} />
                      Filtreleri Temizle
                    </button>
                  )}
                </div>

                {/* Results Count */}
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: '#64748b',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <strong>{filteredClients.length}</strong> müşteri gösteriliyor
                  {filteredClients.length !== clients.length && (
                    <span> (toplam {clients.length} müşteriden)</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</div>
            ) : clients.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><Building /></div>
                <h3 className="empty-state-title">Henüz müşteri yok</h3>
                <p className="empty-state-text">Başlamak için ilk müşterinizi ekleyin</p>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                  <Plus size={18} />
                  Müşteri Ekle
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Ad</th>
                      <th>İrtibat Kişisi</th>
                      <th>E-posta</th>
                      <th>Telefon</th>
                      <th>Bakiye</th>
                      <th>İstatistikler</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr 
                        key={client.id} 
                        data-testid={`client-row-${client.id}`}
                        style={{
                          backgroundColor: client.is_problematic ? '#fef2f2' : 'inherit',
                          borderLeft: client.is_problematic ? '4px solid #ef4444' : 'none'
                        }}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {client.is_problematic && (
                              <span 
                                title={client.problematic_reason || 'Sorunlu müşteri'}
                                style={{ 
                                  color: '#ef4444',
                                  cursor: 'help',
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexShrink: 0
                                }}
                              >
                                <AlertTriangle size={14} />
                              </span>
                            )}
                            <span 
                              title={client.name}
                              style={{ 
                                color: client.is_problematic ? '#dc2626' : 'inherit',
                                fontWeight: 600,
                                maxWidth: '180px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block'
                              }}
                            >
                              {client.name}
                            </span>
                          </div>
                          {client.is_problematic && client.problematic_reason && (
                            <div style={{ 
                              fontSize: '0.7rem', 
                              color: '#dc2626',
                              marginTop: '0.125rem',
                              maxWidth: '180px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {client.problematic_reason}
                            </div>
                          )}
                        </td>
                        <td style={{ maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {client.contact_person || '-'}
                        </td>
                        <td>
                          {client.email ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
                              <Mail size={12} />
                              <span style={{ maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {client.email}
                              </span>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          {client.phone ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
                              <Phone size={12} />
                              {client.phone}
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <strong className={client.balance > 0 ? 'text-danger' : 'text-success'}>
                            ${(client.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </strong>
                        </td>
                        <td>
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '0.25rem',
                            fontSize: '0.875rem'
                          }}>
                            {client.stats_year && (
                              <div style={{ 
                                fontSize: '0.75rem', 
                                color: '#64748b',
                                fontWeight: 600,
                                marginBottom: '0.25rem',
                                paddingBottom: '0.25rem',
                                borderBottom: '1px solid #e2e8f0'
                              }}>
                                {client.stats_year} Yılı
                              </div>
                            )}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: '#3b82f6'
                            }}>
                              <span style={{ fontWeight: 500 }}>Proforma:</span>
                              <span className="badge badge-sent">{client.proforma_count || 0}</span>
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              color: '#10b981'
                            }}>
                              <span style={{ fontWeight: 500 }}>Fatura:</span>
                              <span className="badge badge-approved">{client.invoice_count || 0}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="action-btn" 
                              onClick={() => handleViewLedger(client)}
                              title="View Ledger"
                              data-testid={`view-ledger-${client.id}`}
                            >
                              <Eye size={18} />
                            </button>
                            <button 
                              className="action-btn" 
                              onClick={() => handleEdit(client)}
                              title="Edit"
                              data-testid={`edit-client-${client.id}`}
                            >
                              <Edit2 size={18} />
                            </button>
                            <button 
                              className="action-btn action-btn-danger" 
                              onClick={() => handleDelete(client.id, client.name)}
                              title="Delete"
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="modal-title">
                {selectedClient ? 'Müşteriyi Düzenle' : 'Yeni Müşteri Ekle'}
              </h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                <div className="form-group">
                  <label className="form-label">Şirket Adı *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="client-name-input"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">İrtibat Kişisi</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      data-testid="contact-person-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Vergi Numarası</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                      data-testid="tax-id-input"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="email-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Fatura Adresi</label>
                  <textarea
                    className="form-input"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    rows="3"
                    data-testid="address-input"
                  />
                </div>

                {/* Sorunlu Müşteri Bölümü */}
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: formData.is_problematic ? '#fef2f2' : '#f8fafc',
                  border: formData.is_problematic ? '1px solid #fecaca' : '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}>
                  <div className="form-group" style={{ marginBottom: formData.is_problematic ? '1rem' : 0 }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem',
                      cursor: 'pointer',
                      fontWeight: 500,
                      color: formData.is_problematic ? '#dc2626' : '#374151'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.is_problematic}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          is_problematic: e.target.checked,
                          problematic_reason: e.target.checked ? formData.problematic_reason : ''
                        })}
                        style={{ 
                          width: '18px', 
                          height: '18px',
                          accentColor: '#ef4444'
                        }}
                      />
                      <AlertTriangle size={18} color={formData.is_problematic ? '#ef4444' : '#9ca3af'} />
                      Sorunlu Müşteri
                    </label>
                  </div>
                  
                  {formData.is_problematic && (
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ color: '#dc2626' }}>
                        Sorun Sebebi *
                      </label>
                      <textarea
                        className="form-input"
                        value={formData.problematic_reason}
                        onChange={(e) => setFormData({ ...formData, problematic_reason: e.target.value })}
                        rows="2"
                        placeholder="Gecikmeli ödeme, sözünde durmama vb."
                        style={{
                          borderColor: '#fecaca',
                          background: '#fff'
                        }}
                        required
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-client-btn">
                  {selectedClient ? 'Müşteriyi Güncelle' : 'Müşteri Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ledger Modal */}
      {showLedger && ledgerData && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="ledger-title">
                Cari Hesap - {ledgerData.client.name}
              </h2>
              <button className="modal-close" onClick={() => setShowLedger(false)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ padding: '1rem', background: '#f1f5f9', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Total Invoiced</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                    ${ledgerData.total_invoiced.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#f1f5f9', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Total Paid</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>
                    ${ledgerData.total_paid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ padding: '1rem', background: '#f1f5f9', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Balance</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: ledgerData.balance > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    ${ledgerData.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
              
              {ledgerData.entries.length > 0 ? (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Tür</th>
                        <th>Referans</th>
                        <th>Açıklama</th>
                        <th>Borç</th>
                        <th>Alacak</th>
                        <th>Bakiye</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData.entries.map((entry, index) => (
                        <tr key={index}>
                          <td>{new Date(entry.date).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge badge-${entry.type === 'invoice' ? 'pending' : 'paid'}`}>
                              {entry.type}
                            </span>
                          </td>
                          <td><strong>{entry.reference}</strong></td>
                          <td>{entry.description}</td>
                          <td className="text-danger">
                            {entry.debit > 0 ? `$${entry.debit.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td className="text-success">
                            {entry.credit > 0 ? `$${entry.credit.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td>
                            <strong className={entry.balance > 0 ? 'text-danger' : 'text-success'}>
                              ${entry.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>Henüz işlem yok</p>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={printLedger}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Printer size={18} />
                Yazdır
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setShowLedger(false)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;